import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Center, Loader } from '@mantine/core';
import { useSessionProvider } from '../../../provider/sessionContext.tsx';
import { useGetLegalRequirements } from '../../../entities/Legal/queries/useGetLegalRequirements.tsx';
import LegalAcceptanceWizard from '../LegalAcceptanceWizard/LegalAcceptanceWizard.tsx';

interface TermsAcceptanceGuardProps {
    children: React.ReactNode;
}

export const TermsAcceptanceGuard: React.FC<TermsAcceptanceGuardProps> = observer(({ children }) => {
    const { currentUser, setCurrentUser } = useSessionProvider();

    // Only the backend-computed `termsAccepted === false` triggers the gate. This is
    // resolved at login from the role-based legal requirements, so already-accepted
    // users never hit the requirements endpoint.
    const needsCheck = !!currentUser && currentUser.termsAccepted === false;

    const { data, isLoading } = useGetLegalRequirements(needsCheck);

    const markAccepted = useCallback(() => {
        if (!currentUser) return;
        const updatedUser = {
            ...currentUser,
            termsAccepted: true,
            termsAcceptedDate: new Date().toISOString(),
        };
        setCurrentUser(updatedUser);
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    }, [currentUser, setCurrentUser]);

    // If the backend already considers all required documents accepted, release the gate.
    useEffect(() => {
        if (needsCheck && data?.allAccepted) markAccepted();
    }, [needsCheck, data?.allAccepted, markAccepted]);

    if (!needsCheck) return <>{children}</>;

    if (isLoading || !data) {
        return (
            <Center h="100vh">
                <Loader size="lg" />
            </Center>
        );
    }

    const hasPending = data.documents.some((doc) => !doc.accepted);

    if (!hasPending) return <>{children}</>;

    // Pass the full document set (not just pending) so the stepper count stays
    // stable as each document is accepted; the wizard starts at the first
    // un-accepted document and marks the rest as completed.
    return <LegalAcceptanceWizard documents={data.documents} onCompleted={markAccepted} />;
});

export default TermsAcceptanceGuard;
